"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

interface Category {
	id: string;
	name: string;
	slug: string;
}

interface ProductsFilterProps {
	categories: Category[];
	selectedCategory?: string;
	search?: string;
	sort?: string;
}

const ProductsFilter = ({
	categories = [],
	selectedCategory,
	search,
	sort,
}: ProductsFilterProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [searchValue, setSearchValue] = useState(search || "");
	const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

	// Create a new URLSearchParams instance when params change
	useEffect(() => {
		setSearchValue(search || "");
	}, [search]);

	// Generate URL with filters
	const createFilterUrl = (
		type: "category" | "sort" | "search",
		value: string
	) => {
		const params = new URLSearchParams(searchParams.toString());

		// Reset to page 1 when filters change
		params.set("page", "1");

		if (value) {
			params.set(type, value);
		} else {
			params.delete(type);
		}

		return `/products?${params.toString()}`;
	};

	// Handle search submission
	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		router.push(createFilterUrl("search", searchValue), { scroll: false });
	};

	return (
		<div className="sticky top-24">
			{/* Mobile Filter Toggle */}
			<div className="flex items-center justify-between mb-4 lg:hidden">
				<h2 className="text-lg font-medium text-foreground">Filters</h2>
				<button
					onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
					className="p-2 text-primary rounded-md hover:bg-accent/10"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={1.5}
						stroke="currentColor"
						className="w-6 h-6"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d={
								isMobileFiltersOpen
									? "M6 18L18 6M6 6l12 12"
									: "M10.5 6h9.75M10.5 12h9.75M10.5 18h9.75M3.75 6H6m-.75 6H6m-.75 6H6"
							}
						/>
					</svg>
				</button>
			</div>

			{/* Filters */}
			<div
				className={`space-y-6 transition-all duration-300 ${
					isMobileFiltersOpen ? "block" : "hidden lg:block"
				}`}
			>
				{/* Search */}
				<div className="p-5 rounded-lg shadow-sm bg-card">
					<h3 className="mb-3 text-sm font-medium text-foreground">Search</h3>
					<form onSubmit={handleSearch}>
						<div className="relative">
							<Input
								type="text"
								placeholder="Search products..."
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
								className="pr-10"
							/>
							<button
								type="submit"
								className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary hover:text-primary/80"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="w-5 h-5"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
									/>
								</svg>
							</button>
						</div>
					</form>
				</div>

				{/* Categories */}
				<div className="p-5 rounded-lg shadow-sm bg-card">
					<h3 className="mb-3 text-sm font-medium text-foreground">
						Categories
					</h3>
					<div className="space-y-2">
						<div className="flex items-center">
							<button
								onClick={() => router.push("/products", { scroll: false })}
								className={`cursor-pointer text-sm transition-colors hover:text-primary ${
									!selectedCategory
										? "font-medium text-primary"
										: "text-foreground"
								}`}
							>
								All Categories
							</button>
						</div>
						{categories.map((category) => (
							<div key={category.id} className="flex items-center">
								<button
									onClick={() =>
										router.push(createFilterUrl("category", category.slug), {
											scroll: false,
										})
									}
									className={`cursor-pointer text-sm transition-colors hover:text-primary ${
										selectedCategory === category.slug
											? "font-medium text-primary"
											: "text-foreground"
									}`}
								>
									{category.name}
								</button>
							</div>
						))}
					</div>
				</div>

				{/* Sort */}
				<div className="p-5 rounded-lg shadow-sm bg-card">
					<h3 className="mb-3 text-sm font-medium text-foreground">Sort By</h3>
					<div className="space-y-2">
						<div className="flex items-center">
							<button
								onClick={() =>
									router.push(createFilterUrl("sort", "newest"), {
										scroll: false,
									})
								}
								className={`cursor-pointer text-sm transition-colors hover:text-primary ${
									sort === "newest" || !sort
										? "font-medium text-primary"
										: "text-foreground"
								}`}
							>
								Newest
							</button>
						</div>
						<div className="flex items-center">
							<button
								onClick={() =>
									router.push(createFilterUrl("sort", "price-asc"), {
										scroll: false,
									})
								}
								className={`cursor-pointer text-sm transition-colors hover:text-primary ${
									sort === "price-asc"
										? "font-medium text-primary"
										: "text-foreground"
								}`}
							>
								Price: Low to High
							</button>
						</div>
						<div className="flex items-center">
							<button
								onClick={() =>
									router.push(createFilterUrl("sort", "price-desc"), {
										scroll: false,
									})
								}
								className={`cursor-pointer text-sm transition-colors hover:text-primary ${
									sort === "price-desc"
										? "font-medium text-primary"
										: "text-foreground"
								}`}
							>
								Price: High to Low
							</button>
						</div>
					</div>
				</div>

				{/* Clear Filters */}
				{(selectedCategory || search || (sort && sort !== "newest")) && (
					<div className="p-5 rounded-lg shadow-sm bg-card">
						<Button
							variant="outline"
							className="cursor-pointer w-full transition-all duration-200 border-primary text-primary hover:bg-primary hover:text-white"
							onClick={() => router.push("/products", { scroll: false })}
						>
							Clear All Filters
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default ProductsFilter;
